import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import { ArrowRight, Loader2, Trophy, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const SPORTS = ['Football','Cricket','Badminton','Tennis','Basketball','Pickleball','Volleyball','Table Tennis'];
const SKILLS = ['beginner','intermediate','advanced','pro','mixed'];
const SPORT_ICONS = {
  Football: '⚽', Cricket: '🏏', Badminton: '🏸', Tennis: '🎾', Basketball: '🏀',
  Hockey: '🏑', Volleyball: '🏐', Pickleball: '🏓', 'Table Tennis': '🏓',
};

export default function ArenaxCreateSquad() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', primary_sport: '', city: '', skill_level: 'intermediate', bio: '', max_members: 15,
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.primary_sport || !form.city.trim()) {
      toast.error('Squad name, sport, and city are required.');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/arenax/squads', form);
      toast.success(`${r.data.squad.name} created! Invite your crew →`);
      navigate(`/arenax/squads/${r.data.squad.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create squad');
    } finally { setBusy(false); }
  };

  return (
    <ArenaxLayout>
      <section className="max-w-2xl mx-auto px-5 py-10" data-testid="arenax-create-squad">
        <div className="mb-7">
          <div className="text-[11px] tracking-[0.4em] text-amber-300 font-bold mb-2">FOUND YOUR TRIBE</div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-100">Create a squad.</h1>
          <p className="text-sm text-slate-400 mt-1">Name it. Brand it. Bring your crew. Play every week.</p>
        </div>

        <div className="rounded-2xl border border-cyan-500/15 p-6 space-y-6" style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }}>
          {/* Name */}
          <div>
            <label className="text-[11px] tracking-wider text-slate-400 uppercase font-bold mb-1.5 block">Squad name *</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              data-testid="arenax-create-squad-name"
              maxLength={40}
              placeholder="e.g. Saturday Strikers FC"
              className="arenax-input w-full px-3 py-3 rounded-xl text-lg font-bold" />
          </div>

          {/* Sport */}
          <div>
            <label className="text-[11px] tracking-wider text-slate-400 uppercase font-bold mb-2 block">Primary sport *</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {SPORTS.map(s => (
                <button key={s} onClick={() => setForm({ ...form, primary_sport: s })}
                  data-testid={`arenax-create-squad-sport-${s.toLowerCase().replace(/ /g, '-')}`}
                  className={`p-3 rounded-xl border flex flex-col items-center gap-1 transition-all ${
                    form.primary_sport === s
                      ? 'bg-cyan-500/20 border-cyan-500 ring-2 ring-cyan-500/40'
                      : 'bg-slate-900/40 border-slate-800 hover:border-cyan-500/40'
                  }`}>
                  <span className="text-2xl">{SPORT_ICONS[s] || '🏟️'}</span>
                  <span className="text-[11px] font-bold text-slate-200">{s}</span>
                </button>
              ))}
            </div>
          </div>

          {/* City */}
          <div>
            <label className="text-[11px] tracking-wider text-slate-400 uppercase font-bold mb-1.5 block">City *</label>
            <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
              data-testid="arenax-create-squad-city"
              placeholder="e.g. Bengaluru"
              className="arenax-input w-full px-3 py-2.5 rounded-xl text-sm" />
          </div>

          {/* Skill */}
          <div>
            <label className="text-[11px] tracking-wider text-slate-400 uppercase font-bold mb-2 block">Squad skill bracket</label>
            <div className="flex flex-wrap gap-2">
              {SKILLS.map(sk => (
                <button key={sk} onClick={() => setForm({ ...form, skill_level: sk })}
                  data-testid={`arenax-create-squad-skill-${sk}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold capitalize transition-all ${
                    form.skill_level === sk ? 'bg-amber-500 text-slate-950 shadow-md' : 'bg-slate-900 text-slate-300 border border-slate-700 hover:border-amber-500/50'
                  }`}>{sk}</button>
              ))}
            </div>
          </div>

          {/* Bio */}
          <div>
            <label className="text-[11px] tracking-wider text-slate-400 uppercase font-bold mb-1.5 block">Squad tagline (optional)</label>
            <textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })}
              data-testid="arenax-create-squad-bio"
              maxLength={140} rows={2}
              placeholder="e.g. HSR's most committed 7-a-side crew. Weekly Saturdays. No-shows benched for life."
              className="arenax-input w-full px-3 py-2.5 rounded-xl text-sm resize-none" />
          </div>

          {/* Max members */}
          <div>
            <label className="text-[11px] tracking-wider text-slate-400 uppercase font-bold mb-1.5 block">Max members</label>
            <input type="number" min={2} max={50} value={form.max_members} onChange={e => setForm({ ...form, max_members: Number(e.target.value) })}
              data-testid="arenax-create-squad-maxmembers"
              className="arenax-input w-32 px-3 py-2.5 rounded-xl text-sm" />
          </div>

          <button onClick={submit} disabled={busy} data-testid="arenax-create-squad-submit"
            className="w-full px-4 py-3.5 rounded-xl font-bold text-slate-950 text-sm shadow-xl flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: 'var(--ax-primary-gradient)' }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create Squad <Sparkles className="h-4 w-4" /></>}
          </button>
          <div className="text-[10px] text-slate-600 text-center">You become the captain. You can invite anyone via a shareable link.</div>
        </div>
      </section>
    </ArenaxLayout>
  );
}
